/**
 * Privacy Guard — Client-Side Redaction Engine (Phase 2)
 * 
 * Runs entirely in the browser BEFORE any data is sent to server or LLMs.
 * Reads user settings from localStorage ('privacy_guard_settings').
 * This is the open-source core of the Privacy Guard system.
 * 
 * Priority order:
 *   1. User custom fields (exact match, case-insensitive)
 *   2. UK Pattern Library (regex)
 *   3. Clinician/organisation names (curated prefix patterns)
 *   4. Common name detection (heuristic, best-effort)
 */

const STORAGE_KEY = 'privacy_guard_settings';

// ─── Pre-processor ────────────────────────────────────────────────

function stripFormatting(text) {
  return text
    .replace(/\*\*\*/g, '')
    .replace(/\*\*/g, '')
    .replace(/(?<!\S)\*(?!\s)/g, '').replace(/(?<!\s)\*(?!\S)/g, '')
    .replace(/~~/g, '')
    .replace(/\u200B/g, '');
}

// ─── Checksum validation (A3) — mirrors redactus-checksum.js ──────
// Structured IDs only redact when their checksum/format is valid → cuts
// false positives on random digit runs that merely match the ID shape.
function csDigits(s) { return (s || '').replace(/\D/g, ''); }
function csNHS(v) {
  const d = csDigits(v);
  if (d.length !== 10 || /^0+$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(d[i], 10) * (10 - i);
  let c = 11 - (sum % 11);
  if (c === 11) c = 0;
  if (c === 10) return false;
  return c === parseInt(d[9], 10);
}
function csLuhn(v) {
  const d = csDigits(v);
  if (d.length < 12 || d.length > 19) return false;
  let sum = 0, dbl = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = parseInt(d[i], 10);
    if (dbl) { n *= 2; if (n > 9) n -= 9; }
    sum += n; dbl = !dbl;
  }
  return sum % 10 === 0;
}
function csIBAN(v) {
  const iban = (v || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const re = iban.slice(4) + iban.slice(0, 4);
  const num = re.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());
  let r = 0;
  for (let i = 0; i < num.length; i++) r = (r * 10 + parseInt(num[i], 10)) % 97;
  return r === 1;
}
const CS_NINO_BAD_PREFIX = new Set(['BG', 'GB', 'NK', 'KN', 'TN', 'NT', 'ZZ']);
function csNINO(v) {
  const s = (v || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{6}[A-D]$/.test(s)) return false;
  const p = s.slice(0, 2);
  if (CS_NINO_BAD_PREFIX.has(p)) return false;
  if ('DFIQUV'.includes(p[0])) return false;
  if ('DFIOQUV'.includes(p[1])) return false;
  if (p[1] === 'O') return false;
  return true;
}
const CS_VALIDATORS = { nhs_number: csNHS, ni_number: csNINO, eu_iban: csIBAN, card: csLuhn };
function validateChecksum(id, value) {
  const fn = CS_VALIDATORS[id];
  if (!fn) return null;
  return fn(value);
}

// ─── UK Pattern Library ───────────────────────────────────────────

const UK_PATTERNS = {
  nhs_number: {
    regex: /\b\d{3}\s?\d{3}\s?\d{4}\b/g,
    token: '[NHS-REDACTED]',
    label: 'NHS Number',
    checksum: 'nhs_number'
  },
  ni_number: {
    regex: /\b[A-Z]{2}\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]\b/gi,
    token: '[NI-REDACTED]',
    label: 'NI Number',
    checksum: 'ni_number'
  },
  uk_postcode: {
    regex: /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/gi,
    token: '[POSTCODE-REDACTED]',
    label: 'Postcode'
  },
  phone: {
    regex: /(?<!\d)(?:(?:\+44|\+1)\s?(?:\(\d{3}\)\s?|\d)[\d \-\u2011\u2013\(\)]{7,14}|(?<!\d)0\d{2,4}[\s]?\d{3,4}[\s]?\d{3,4}|\(\d{3}\)\s?\d{3}[\s.\-\u2011\u2013]\d{4})/g,
    token: '[PHONE-REDACTED]',
    label: 'Phone'
  },
  email: {
    regex: /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Z|a-z]{2,}/g,
    token: '[EMAIL-REDACTED]',
    label: 'Email'
  },
  dob: {
    // Only matches dates near DOB/Patient/Birth context — handled separately
    regex: /\b\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}\b/g,
    token: '[DOB-REDACTED]',
    label: 'Date of Birth',
    contextRequired: true
  },
  uk_address: {
    regex: /\b\d{1,4}\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\s+(?:Street|St|Lane|Ln|Road|Rd|Avenue|Ave|Drive|Dr|Close|Way|Place|Pl|Crescent|Terrace|Court|Ct|Gardens|Grove|Park|Mews|Row|Rise|Hill|Walk|Square|Passage|Yard|Boulevard|Blvd)\b/gi,
    token: '[ADDRESS-REDACTED]',
    label: 'Address'
  },
  flat_address: {
    regex: /\b(?:Flat|Apartment|Apt\.?|Unit|Suite)\s+\w+,?\s*(?:\d{1,4}\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\s+(?:Street|St|Lane|Ln|Road|Rd|Avenue|Ave|Drive|Dr|Close|Way|Place|Pl|Crescent|Terrace|Court|Ct|Gardens|Grove|Park|Mews|Row|Rise|Hill|Walk|Square|Boulevard|Blvd))?/gi,
    token: '[ADDRESS-REDACTED]',
    label: 'Address'
  }
};

// ─── US Pattern Library ───────────────────────────────────────────

const US_PATTERNS = {
  us_ssn: {
    regex: /\b\d{3}[\-\u2011\u2013]\d{2}[\-\u2011\u2013]\d{4}\b/g,
    token: '[SSN-REDACTED]',
    label: 'SSN'
  },
  us_address_full: {
    regex: /\b\d{1,5}\s+(?:[NSEW]{1,2}\.?\s+)?[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2}\s+(?:Street|St|Lane|Ln|Road|Rd|Avenue|Ave|Drive|Dr|Close|Way|Place|Pl|Court|Ct|Boulevard|Blvd|Circle|Cir|Parkway|Pkwy|Highway|Hwy)\.?(?:,?\s+(?:(?:Suite|Ste|Apt|Unit|#)\s*\w+,?\s*)?[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)?/gi,
    token: '[ADDRESS-REDACTED]',
    label: 'Address'
  },
  us_city_state_zip: {
    regex: /(?:,\s*)?[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?/g,
    token: '[ADDRESS-REDACTED]',
    label: 'Address'
  },
  us_phone: {
    regex: /(?<!\d)\(?\d{3}\)?[\s.\-\u2011\u2013]\d{3}[\s.\-\u2011\u2013]\d{4}(?!\d)/g,
    token: '[PHONE-REDACTED]',
    label: 'Phone'
  },
  us_zip: {
    regex: /\b\d{5}(?:-\d{4})?\b/g,
    token: '[ZIP-REDACTED]',
    label: 'ZIP Code',
    contextRequired: true,
    contextKeywords: ['zip', 'address', 'addr', 'state', 'city', 'street', 'ave', 'blvd', 'rd', 'dr', 'apt']
  },
  us_date: {
    regex: /\b(?:0?[1-9]|1[0-2])\/(?:0?[1-9]|[12]\d|3[01])\/(?:19|20)\d{2}\b/g,
    token: '[DATE-REDACTED]',
    label: 'US Date',
    contextRequired: true
  },
  mrn: {
    regex: /\b\d{5,10}\b/g,
    token: '[MRN-REDACTED]',
    label: 'MRN',
    contextRequired: true,
    contextKeywords: ['mrn','medical record','med rec','patient id','patient no','hospital number']
  },
  hospital_ref: {
    regex: /\b[A-Z]{1,4}[\-\u2011\u2013]\d{4}[\-\u2011\u2013]\d{2,6}(?:[\-\u2011\u2013]\d{2,4})?\b/g,
    token: '[REF-REDACTED]',
    label: 'Hospital Reference',
    contextRequired: true,
    contextKeywords: ['hospital number','hospital no','patient id','mrn','reference','hosp']
  },
  insurance_id: {
    regex: /\b[A-Z]{1,6}[\-\u2011\u2013]\d{2,10}(?:[\-\u2011\u2013]\d{2,10})*(?:[\-\u2011\u2013][A-Z]{2,4})?\b/g,
    token: '[INSURANCE-REDACTED]',
    label: 'Insurance/Policy ID',
    contextRequired: true,
    contextKeywords: ['insurance','policy','membership','plan','member','provider','group','grp']
  },
  auth_claim: {
    regex: /\b[A-Z\d]{2,5}[\-\u2011\u2013](?:[A-Z]{2,5}[\-\u2011\u2013])?[\d\-\u2011\u2013]{4,25}\b/g,
    token: '[CLAIM-REDACTED]',
    label: 'Auth/Claim Code',
    contextRequired: true,
    contextKeywords: ['auth','authorization','authorisation','claim','pre-auth','prior auth','approval','claim id']
  },
  transaction_id: {
    regex: /\b(?:TXN|REF|INV)[\-\u2011\u2013][\d\-\u2011\u2013]{8,25}\b/gi,
    token: '[TRANSACTION-REDACTED]',
    label: 'Transaction ID'
  },
  card_last4: {
    regex: /\b\d{4}\b/g,
    token: '[CARD-REDACTED]',
    label: 'Card Digits',
    contextRequired: true,
    contextKeywords: ['last 4','last four','card','digits']
  }
};

// ─── EU Pattern Library ───────────────────────────────────────────

const EU_PATTERNS = {
  eu_iban: {
    regex: /\b[A-Z]{2}\d{2}\s?(?:\d{4}\s?){3,7}\d{1,4}\b/gi,
    token: '[IBAN-REDACTED]',
    label: 'IBAN',
    checksum: 'eu_iban'
  },
  eu_healthcard: {
    regex: /\b\d{10,20}\b/g,
    token: '[HEALTHCARD-REDACTED]',
    label: 'Health Card',
    contextRequired: true,
    contextKeywords: ['ehic', 'health card', 'insurance', 'versicherung', 'carte vitale', 'tessera sanitaria']
  },
  eu_date: {
    regex: /\b\d{1,2}\.\d{1,2}\.\d{2,4}\b/g,
    token: '[DATE-REDACTED]',
    label: 'EU Date',
    contextRequired: true
  }
};

// Merged pattern library for the engine
// US address patterns run first (more specific — catch street + city/state/ZIP in one token)
// EU patterns before UK so IBAN runs before phone (prevents phone matching within spaced IBANs)
const ALL_PATTERNS = { ...US_PATTERNS, ...EU_PATTERNS, ...UK_PATTERNS };

// Context keywords that indicate a date is PII (DOB) rather than a clinical date
const DOB_CONTEXT_KEYWORDS = [
  'dob', 'date of birth', 'born', 'birthday', 'patient', 'd.o.b', 'd.o.b.'
];

// ─── Clinician/Organisation Prefix Patterns ───────────────────────
// Curated structured-context patterns, NOT naive keyword search

const CLINICIAN_PREFIXES = [
  /\bseen by dr\.?\s+[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bconsultant:?\s*dr\.?\s+[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\breferring (?:clinician|physician):?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bgp:?\s*dr\.?\s+[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bgp\s+name:?\s*dr\.?\s+[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\battending(?:\s+physician)?:?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bdoctor:?\s*[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\breported by:?\s*dr\.?\s+[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bordering\s+(?:physician|provider):?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bphysician:?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bprovider:?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bpractitioner:?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bclinician:?\s*(?:dr\.?\s+)?[A-Z][\w.]+(?:\s+[A-Z][\w.]+){0,4}/gi,
  /\bDr\.?\s+[A-Z][a-z]+\s+[A-Z]\.?\s*[A-Z][a-z]+/g,
];

const HOSPITAL_PREFIXES = [
  /\bat\s+(?:the\s+)?(?:[A-Z][a-zA-Z'.]+\s+){0,4}(?:hospital|clinic|surgery|medical cent(?:re|er)|health cent(?:re|er)|infirmary)\b/gi,
  /\bhospital:\s*(?:[A-Z][a-zA-Z'.]+\s*)+/gi,
  /\bclinic:\s*(?:[A-Z][a-zA-Z'.]+\s*)+/gi,
  /\bgp\s+practice:?\s*(?:[A-Z][a-zA-Z'.]+\s*)+/gi,
  /\b(?:[A-Z][a-zA-Z'.]+\s+){1,4}(?:medical cent(?:re|er)|hospital|clinic|infirmary)(?:\s*[\u2013\u2014\-]\s*[A-Z][a-zA-Z\s]+)?/gi,
];

const INSURER_PREFIXES = [
  /\binsurance +provider:? *[A-Z][a-zA-Z'.]+(?:[ ]+[A-Z][a-zA-Z'.]+)*/gi,
  /\binsurer:? *[A-Z][a-zA-Z'.]+(?:[ ]+[A-Z][a-zA-Z'.]+)*/gi,
];

// Dates that co-occur with clinic/ID context on same line
const CO_OCCURRING_DATE_CONTEXT = [
  'hospital', 'clinic', 'seen by', 'consultant', 'gp:', 'surgery',
  'appointment', 'attended', 'visit', 'referred'
];

// ─── Common Name Detection (Heuristic) ───────────────────────────

const NAME_PATTERNS = [
  /\bpatient:\s*(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bname:\s*(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bdear\s+(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bpatient\s+name:\s*(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bnext of kin(?!\s+(?:phone|email|address|mobile|tel)):?\s*(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bemergency contact(?!\s+(?:phone|email|address|mobile|tel)):?\s*(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bguarantor(?:\s+name)?:?\s*(?:Mr\.?\s+|Mrs?\.?\s+|Ms\.?\s+|Dr\.?\s+)?([A-Z][a-z]+(?:[^\S\n]+[A-Z][a-z]*\.?){0,4})/gi,
  /\bcardholder(?:\s+name)?:?\s*([A-Z][A-Z\s]{1,30})\b/gi,
  /\bI'?m\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3})/g,
  /\bMy name is\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3})/gi,
  /\bI am\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3})/gi
];

// ─── Core Engine ──────────────────────────────────────────────────

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* corrupted */ }
  // Privacy-first default: the Privacy Guard UI shows ON by default, but a
  // fresh browser/account has no stored key. Returning null here used to make
  // redaction silently no-op while the UI claimed "Active". Instead, default to
  // ENABLED with every built-in pattern on, so first-run users are actually
  // protected. A deliberate OFF is always persisted, so this only affects the
  // never-configured case.
  return {
    enabled: true,
    customFields: [],
    patterns: Object.keys(ALL_PATTERNS).map(id => ({ id, enabled: true })),
  };
}

function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    // localStorage unavailable (Node tests, browser private mode, quota exceeded).
    // Counter updates are best-effort — never block redaction itself.
  }
}

/**
 * Main redaction function.
 * @param {string} text - Raw text to redact
 * @param {object|null} overrideSettings - Optional. If provided, bypass the
 *   localStorage settings gate and use these settings instead. Used by
 *   `redactTextAlways` (admin Agent Report path) so filenames are stripped
 *   regardless of whether the admin's browser has Privacy Guard enabled.
 * @returns {{ redactedText: string, blockedFragments: Array, tokens: Array, totalRedacted: number }}
 */
export function redactText(text, overrideSettings = null) {
  if (!text || typeof text !== 'string') {
    return { redactedText: text, blockedFragments: [], tokens: [], totalRedacted: 0 };
  }

  const settings = overrideSettings || loadSettings();
  if (!settings || !settings.enabled) {
    return { redactedText: text, blockedFragments: [], tokens: [], totalRedacted: 0 };
  }

  let result = stripFormatting(text);
  const blocked = [];
  const tokenCounts = {};

  const track = (type, fragment) => {
    blocked.push({ type, fragment });
    tokenCounts[type] = (tokenCounts[type] || 0) + 1;
  };

  // ── Priority 1: User Custom Fields (exact match, case-insensitive) ──
  const customFields = settings.customFields || [];
  for (const field of customFields) {
    if (field.value && field.value.trim()) {
      const val = field.value.trim();
      // Escape regex special chars for exact match
      const escaped = val.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escaped, 'gi');
      const matches = result.match(regex);
      if (matches) {
        matches.forEach(m => track('PRIVATE', m));
        result = result.replace(regex, '[PRIVATE-REDACTED]');
      }
    }
  }

  // ── Priority 2: UK Pattern Library (regex) ──
  const patterns = settings.patterns || [];
  const enabledPatternIds = new Set(patterns.filter(p => p.enabled).map(p => p.id));

  // Run checksum-gated structured-ID patterns (NHS/NI/IBAN) FIRST so a genuine
  // ID is recognised and correctly labelled before a generic fallback (e.g.
  // us_phone, which also matches 3-3-4 spaced digits) can pre-empt it. sort()
  // is stable, so relative order is otherwise preserved.
  const orderedEntries = Object.entries(ALL_PATTERNS)
    .sort((a, b) => (b[1].checksum ? 1 : 0) - (a[1].checksum ? 1 : 0));

  for (const [patternId, patternDef] of orderedEntries) {
    if (!enabledPatternIds.has(patternId)) continue;

    if (patternDef.contextRequired) {
      if (patternDef.contextKeywords) {
        // Context-aware redaction for patterns with custom context keywords
        result = redactWithContext(result, patternDef, track);
      } else {
        // DOB-style: only redact dates in PII context
        result = redactDatesInContext(result, patternDef, track);
      }
    } else {
      const regex = new RegExp(patternDef.regex.source, patternDef.regex.flags);
      if (patternDef.checksum) {
        // Checksum gate (A3): only redact matches that pass their checksum/format.
        const matches = result.match(regex) || [];
        for (const m of matches) {
          if (validateChecksum(patternDef.checksum, m) === false) continue;
          track(patternDef.label, m);
          result = result.replace(m, patternDef.token);
        }
      } else {
        const matches = result.match(regex);
        if (matches) {
          matches.forEach(m => track(patternDef.label, m));
          result = result.replace(regex, patternDef.token);
        }
      }
    }
  }

  // ── Priority 3: Insurer/Clinician/Organisation (insurer first — more specific) ──
  // Protect existing redaction tokens from being re-matched by later patterns
  const tokenPlaceholders = [];
  result = result.replace(/\[[A-Z\-]+-REDACTED\]/g, (match) => {
    const placeholder = `__PLACEHOLDER_${tokenPlaceholders.length}__`;
    tokenPlaceholders.push(match);
    return placeholder;
  });

  for (const regex of INSURER_PREFIXES) {
    const re = new RegExp(regex.source, regex.flags);
    const matches = result.match(re);
    if (matches) {
      for (const m of matches) {
        if (m.includes('__PLACEHOLDER_')) continue;
        track('Insurer', m);
        result = result.replace(m, '[INSURER-REDACTED]');
      }
    }
  }

  for (const regex of CLINICIAN_PREFIXES) {
    const re = new RegExp(regex.source, regex.flags);
    const matches = result.match(re);
    if (matches) {
      for (const m of matches) {
        if (m.includes('__PLACEHOLDER_')) continue;
        track('Clinician', m);
        result = result.replace(m, '[CLINICIAN-REDACTED]');
      }
    }
  }

  for (const regex of HOSPITAL_PREFIXES) {
    const re = new RegExp(regex.source, regex.flags);
    const matches = result.match(re);
    if (matches) {
      for (const m of matches) {
        if (m.includes('__PLACEHOLDER_')) continue;
        track('Hospital', m);
        result = result.replace(m, '[HOSPITAL-REDACTED]');
      }
    }
  }

  for (const regex of INSURER_PREFIXES) {
    const re = new RegExp(regex.source, regex.flags);
    const matches = result.match(re);
    if (matches) {
      for (const m of matches) {
        if (m.includes('__PLACEHOLDER_')) continue;
        track('Insurer', m);
        result = result.replace(m, '[INSURER-REDACTED]');
      }
    }
  }

  // Restore placeholders
  tokenPlaceholders.forEach((token, i) => {
    result = result.replace(`__PLACEHOLDER_${i}__`, token);
  });

  // Redact co-occurring dates (dates on same line as clinic/hospital context)
  result = redactCoOccurringDates(result, track);

  // ── Priority 4: Common Name Detection (heuristic, best-effort) ──
  for (const regex of NAME_PATTERNS) {
    const re = new RegExp(regex.source, regex.flags);
    let match;
    while ((match = re.exec(result)) !== null) {
      if (match[1] && !match[1].includes('REDACTED')) {
        track('Name', match[1]);
        result = result.replace(match[1], '[NAME-REDACTED]');
      }
    }
  }

  // ── Update counter in localStorage ──
  const totalRedacted = blocked.length;
  if (totalRedacted > 0) {
    updateRedactionCounter(settings, totalRedacted);
  }

  const tokens = Object.entries(tokenCounts).map(([type, count]) => ({ type, count }));

  return { redactedText: result, blockedFragments: blocked, tokens, totalRedacted };
}

/**
 * Context-aware redaction for patterns with custom context keywords.
 * Only redacts matches when they appear near one of the pattern's context keywords.
 */
function redactWithContext(text, patternDef, track) {
  const keywords = patternDef.contextKeywords || [];
  const lines = text.split('\n');
  const processed = lines.map(line => {
    const lower = line.toLowerCase();
    const hasContext = keywords.some(kw => lower.includes(kw));
    if (!hasContext) return line;

    const regex = new RegExp(patternDef.regex.source, patternDef.regex.flags);
    const matches = line.match(regex);
    if (matches) {
      matches.forEach(m => track(patternDef.label, m));
      return line.replace(regex, patternDef.token);
    }
    return line;
  });
  return processed.join('\n');
}

/**
 * Context-aware DOB redaction.
 * Only redacts dates when they appear within close proximity to DOB-related keywords.
 */
function redactDatesInContext(text, patternDef, track) {
  // Match dates that are within ~40 chars of a DOB keyword
  for (const kw of DOB_CONTEXT_KEYWORDS) {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Look for keyword followed by a date within ~40 chars
    const proximityRegex = new RegExp(
      escaped + '[:\\s,;.\\-]{0,10}' + patternDef.regex.source,
      'gi'
    );
    const matches = text.match(proximityRegex);
    if (matches) {
      for (const m of matches) {
        const dateMatch = m.match(new RegExp(patternDef.regex.source));
        if (dateMatch) {
          track(patternDef.label, dateMatch[0]);
          text = text.replace(dateMatch[0], patternDef.token);
        }
      }
    }
  }
  return text;
}

/**
 * Redact dates that co-occur with clinical location context on the same line.
 * "on 09/02/2026" next to "hospital" or "clinic" → [DATE-REDACTED]
 */
function redactCoOccurringDates(text, track) {
  const dateRegex = /\b\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}\b/g;
  const lines = text.split('\n');
  const processed = lines.map(line => {
    const lower = line.toLowerCase();
    const hasContext = CO_OCCURRING_DATE_CONTEXT.some(kw => lower.includes(kw));
    if (!hasContext) return line;

    const matches = line.match(dateRegex);
    if (matches) {
      matches.forEach(m => track('Co-occurring Date', m));
      return line.replace(dateRegex, '[DATE-REDACTED]');
    }
    return line;
  });
  return processed.join('\n');
}

/**
 * Increment the redaction counter in localStorage.
 */
function updateRedactionCounter(settings, count) {
  settings.redactionCount = (settings.redactionCount || 0) + count;
  settings.queryCount = (settings.queryCount || 0) + 1;
  settings.lastRedaction = new Date().toISOString();
  saveSettings(settings);
}

/**
 * Check if Privacy Guard is currently enabled.
 */
export function isPrivacyGuardEnabled() {
  const settings = loadSettings();
  return settings?.enabled ?? false;
}

/**
 * Get current redaction stats from localStorage.
 */
export function getRedactionStats() {
  const settings = loadSettings();
  return {
    enabled: settings?.enabled ?? false,
    redactionCount: settings?.redactionCount ?? 0,
    queryCount: settings?.queryCount ?? 0,    lastRedaction: settings?.lastRedaction ?? null
  };
}


/**
 * Force redaction with all built-in patterns enabled, regardless of admin's
 * Privacy Guard settings. Used by the admin Agent Report (May 6, 2026 — to
 * support Mode 4 language-misfire reporting where filenames may contain PHI
 * and the admin's browser may not have Privacy Guard configured).
 *
 * Mirrors the pattern-id list from PrivacyGuard.js BASE_PATTERNS +
 * REGION_PATTERNS.UK + US + EU + the US_PATTERNS extension set in this file.
 * Anything matched by ALL_PATTERNS gets stripped.
 *
 * @param {string} text - Raw text (typically a filename) to redact.
 * @returns {{ redactedText, blockedFragments, tokens, totalRedacted }}
 */
export function redactTextAlways(text) {
  const FORCED_PATTERN_IDS = [
    // Base
    'phone', 'email', 'dob',
    // UK
    'nhs_number', 'ni_number', 'uk_postcode', 'uk_address',
    // US
    'us_ssn', 'us_zip', 'us_date', 'us_address',
    // EU
    'eu_iban', 'eu_healthcard', 'eu_date',
    // Healthcare/admin extension
    'hospital_number', 'insurance_id', 'auth_claim', 'transaction_id', 'card_last4',
  ];
  const synthetic = {
    enabled: true,
    customFields: [],
    patterns: FORCED_PATTERN_IDS.map(id => ({ id, enabled: true })),
  };
  return redactText(text, synthetic);
}
